import React, { JSX, useRef, useState } from 'react';
import { InputProps, Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from '../Form';
import classNames from 'classnames';
import { useClickAway } from 'react-use';
import FormInputSearchDropdownCore, {
  FormInputSearchDropdownProps
} from './FormInputSearchDropdownCore';
import { sanitize } from '@ibe/services';
import { getErrorMessage } from './utils';

const FormInputSearchDropdown = (
  props: FormInputSearchDropdownProps & Omit<InputProps, 'tag'>
): JSX.Element | null => {
  const {
    name,
    baseName,
    label,
    boldLabel,
    errorsProp,
    showErrors,
    showErrorIcon,
    validateOnBlurOnly,
    showSuccessState
  } = props;

  const {
    register,
    errors,
    control,
    setValue,
    exposeSeparateFieldValue,
    formState,
    trigger
  } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};
  const [collapseOpen, setCollapseOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldName = baseName ?? name;

  useClickAway(containerRef, async () => {
    setCollapseOpen(false);
    setIsFocused(false);
    setHasBeenBlurred(true);
    if (trigger) {
      await trigger(fieldName);
    }
  });

  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  const hasError = !!formErrors?.[fieldName];
  const isTouched = !!touchedFields?.[fieldName];
  const fieldValue = _formValues?.[fieldName];
  const hasValue = typeof fieldValue === 'string' ? fieldValue.length > 0 : !!fieldValue;

  const displayError = validateOnBlurOnly
    ? showErrors && hasError && (isSubmitted || isTouched || hasBeenBlurred) && !isFocused
    : showErrors && hasError;

  const displaySuccess = showSuccessState ? !hasError && hasValue && !isFocused : false;

  return (
    <div
      ref={containerRef}
      role="group"
      onFocus={() => setIsFocused(true)}
      onBlur={async e => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
          setHasBeenBlurred(true);
          if (trigger) await trigger(fieldName);
        }
      }}
      className={classNames('form-input', 'form-input-' + name, {
        'input--error': displayError,
        'form-input--errors': displayError,
        'form-input--touched': isTouched,
        'form-input--dirty': dirtyFields?.[fieldName],
        'form-input--form-submitted': isSubmitted,
        'form-input--success': displaySuccess
      })}
    >
      {!!label && (
        <Label
          className={`${displayError ? 'form__error__top ' : ''}${
            boldLabel ? 'font-weight-bold' : ''
          }`}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }): JSX.Element => (
          <div className="position-relative">
            <FormInputSearchDropdownCore
              {...props}
              field={field}
              collapseOpen={collapseOpen}
              setCollapseOpen={setCollapseOpen}
              exposeSeparateFieldValue={exposeSeparateFieldValue}
              setValue={setValue}
            />
            {!!field.value && (
              <FontAwesomeIcon
                className="form-input__clear"
                style={{ right: displaySuccess || (showErrorIcon && displayError) ? '40px' : '' }}
                icon={faTimes}
                onClick={(): void => {
                  if (setValue) {
                    setValue(name, '');
                  }
                }}
              />
            )}
            {displaySuccess && (
              <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />
            )}
          </div>
        )}
      />
      {displayError && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
      {showErrorIcon && displayError && (
        <FontAwesomeIcon icon={faExclamationTriangle} className="form-input__error-icon" />
      )}
      {!formErrors?.[fieldName] &&
        (isTouched || isSubmitted) &&
        !!_formValues?.[fieldName] &&
        !showSuccessState && <span className="form-input__success" />}
    </div>
  );
};

export default FormInputSearchDropdown;
